// components/HeartAnimation.tsx
import React, { useEffect, useState } from 'react';
interface HeartAnimationProps {
  isActive: boolean;
}
export const HeartAnimation: React.FC<HeartAnimationProps> = ({
  isActive
}) => {
  const [shouldAnimate, setShouldAnimate] = useState(false);
  useEffect(() => {
    if (isActive) {
      setShouldAnimate(true);
      const timer = setTimeout(() => {
        setShouldAnimate(false);
      }, 1000); // Shorter duration
      return () => clearTimeout(timer);
    }
  }, [isActive]);
  if (!shouldAnimate) return null;

  // Fewer particles - more subtle
  const particles = Array.from({
    length: 8
  }, (_, i) => {
    const angle = i / 8 * Math.PI * 2;
    const distance = 25 + Math.random() * 20; // Shorter distance
    const tx = Math.cos(angle) * distance;
    const ty = Math.sin(angle) * distance;
    const size = 3 + Math.random() * 4; // Smaller particles
    const delay = Math.random() * 0.3;
    return <div key={i} style={{
      position: 'absolute',
      width: `${size}px`,
      height: `${size}px`,
      background: '#ff4081',
      borderRadius: '50%',
      top: '50%',
      left: '50%',
      animation: `floatParticle 0.8s ease-out ${delay}s forwards`,
      transform: 'translate(-50%, -50%)'
    }} />;
  });

  // Fewer hearts - more subtle
  const hearts = Array.from({
    length: 4
  }, (_, i) => {
    const angle = i / 4 * Math.PI * 2;
    const distance = 30 + Math.random() * 25; // Shorter distance
    const tx = Math.cos(angle) * distance;
    const ty = Math.sin(angle) * distance;
    const size = 8 + Math.random() * 6; // Smaller hearts
    const delay = 0.1 + Math.random() * 0.2;
    const rotation = Math.random() * 180;
    return <div key={i} style={{
      position: 'absolute',
      width: `${size}px`,
      height: `${size}px`,
      top: '50%',
      left: '50%',
      animation: `floatHeart 1s ease-out ${delay}s forwards`,
      transform: 'translate(-50%, -50%)',
      opacity: 0.8 // More transparent
    }}>
        <svg viewBox="0 0 24 24" fill="#ff4081" style={{
        filter: 'drop-shadow(0 1px 2px rgba(255, 64, 129, 0.2))'
      }}>
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
        </svg>
      </div>;
  });
  return <div style={{
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80px',
    // Smaller container
    height: '80px',
    pointerEvents: 'none',
    zIndex: 1
  }} data-sentry-component="HeartAnimation" data-sentry-source-file="HeartAnimation.tsx">
      {particles}
      {hearts}

      <style>
        {`
          @keyframes floatParticle {
            0% {
              opacity: 0;
              transform: translate(-50%, -50%) scale(0);
            }
            20% {
              opacity: 0.7;
              transform: translate(-50%, -50%) scale(1);
            }
            100% {
              opacity: 0;
              transform: translate(calc(-50% + var(--tx, 0px)), calc(-50% + var(--ty, 0px))) scale(0.2);
            }
          }

          @keyframes floatHeart {
            0% {
              opacity: 0;
              transform: translate(-50%, -50%) scale(0) rotate(0deg);
            }
            30% {
              opacity: 0.8;
              transform: translate(-50%, -50%) scale(1) rotate(5deg);
            }
            100% {
              opacity: 0;
              transform: translate(calc(-50% + var(--tx, 0px)), calc(-50% + var(--ty, 0px))) scale(0.3) rotate(var(--rotation, 0deg));
            }
          }

          /* Particle positions - closer to center */
          div:nth-child(1) { --tx: -20px; --ty: -20px; }
          div:nth-child(2) { --tx: 0px; --ty: -30px; }
          div:nth-child(3) { --tx: 20px; --ty: -20px; }
          div:nth-child(4) { --tx: 30px; --ty: 0px; }
          div:nth-child(5) { --tx: 20px; --ty: 20px; }
          div:nth-child(6) { --tx: 0px; --ty: 30px; }
          div:nth-child(7) { --tx: -20px; --ty: 20px; }
          div:nth-child(8) { --tx: -30px; --ty: 0px; }

          /* Heart positions - closer and more subtle */
          div:nth-child(9) { --tx: -25px; --ty: -15px; --rotation: -8deg; }
          div:nth-child(10) { --tx: 15px; --ty: -25px; --rotation: 5deg; }
          div:nth-child(11) { --tx: 25px; --ty: 10px; --rotation: -5deg; }
          div:nth-child(12) { --tx: -10px; --ty: 25px; --rotation: 8deg; }
        `}
      </style>
    </div>;
};